import React, { useCallback } from 'react'
import { Box, Checkbox, FormControlLabel, Typography } from '@mui/material'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '@/constants/colors'

export interface TeamDataItem {
  teamId: string
  teamName: string
}

export interface TeamDataSelectorProps {
  teams: TeamDataItem[]
  selectedTeamIds: string[] // Array of selected team IDs
  onTeamChange: (selectedTeamIds: string[]) => void
  className?: string
}

/**
 * TeamDataSelector component
 * Checkbox selector for choosing which team's data to display in visualizations
 * Supports multi-select: multiple teams can be selected/unselected independently
 */
export const TeamDataSelector: React.FC<TeamDataSelectorProps> = ({
  teams,
  selectedTeamIds = [],
  onTeamChange,
  className = ''
}) => {
  // Define handleChange before early returns to comply with Rules of Hooks
  const handleChange = useCallback(
    (teamId: string, event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked

      if (isChecked) {
        // Add team ID to selected array if not already present
        if (!selectedTeamIds.includes(teamId)) {
          onTeamChange([...selectedTeamIds, teamId])
        }
      } else {
        // Remove team ID from selected array
        onTeamChange(selectedTeamIds.filter((id) => id !== teamId))
      }
    },
    [selectedTeamIds, onTeamChange]
  )

  if (!teams || teams.length === 0) {
    return null
  }

  // Only show selector if there's more than one team (otherwise it's redundant)
  if (teams.length <= 1) {
    return null
  }

  return (
    <Box
      className={className}
      sx={{
        marginBottom: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'flex-start'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 2,
          alignItems: 'flex-start'
        }}
      >
        {teams.map((team) => (
          <FormControlLabel
            key={team.teamId}
            control={
              <Checkbox
                checked={selectedTeamIds.includes(team.teamId)}
                onChange={(e) => handleChange(team.teamId, e)}
                sx={{
                  color: INTERAX_COLORS.white,
                  padding: '4px',
                  '&.Mui-checked': {
                    color: INTERAX_COLORS.sun
                  }
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                sx={{
                  color: INTERAX_COLORS.white,
                  fontFamily: INTERAX_TYPOGRAPHY.body3.fontFamily,
                  fontSize: INTERAX_TYPOGRAPHY.body3.fontSize,
                  fontWeight: INTERAX_TYPOGRAPHY.body3.fontWeight,
                  lineHeight: INTERAX_TYPOGRAPHY.body3.lineHeight
                }}
              >
                {team.teamName}
              </Typography>
            }
            sx={{
              margin: 0,
              '& .MuiFormControlLabel-label': {
                marginLeft: '6px'
              }
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
